import { Check, ChevronDown } from "lucide-react";

import { Menu, MenuItem } from "@/components/ui/menu";
import { LOCALES, type Locale } from "@/constants/i18n";
import { useLocaleStore, useTranslation } from "@/store/locale";

/**
 * The language pill, opening the app's own menu.
 *
 * It was a native `<select>` held invisibly over the pill, which opened the
 * operating system's list — its own type, its own grey, its own selection
 * colour — beside a pill drawn in ours. Every other dropdown in the app is
 * drawn here, so this one is too.
 */
/** The flag stands in for the name, as the legacy pill has it. */
const FLAGS: Record<Locale, string> = { en: "\u{1F1FA}\u{1F1F8}", fil: "\u{1F1F5}\u{1F1ED}" };

export function LanguageSelect({
  side = "bottom",
  align = "end",
}: {
  /** `top` when the pill sits at the foot of the screen and has no room below. */
  side?: "top" | "bottom";
  align?: "start" | "end";
} = {}) {
  const t = useTranslation();
  const locale = useLocaleStore((state) => state.locale);
  const setLocale = useLocaleStore((state) => state.setLocale);

  const current = LOCALES.find((entry) => entry.code === locale) ?? LOCALES[0];

  return (
    <Menu
      side={side}
      align={align}
      width="w-44"
      trigger={
        <span
          aria-label={t("language.label")}
          className="bg-surface-raised border-line inline-flex h-10 items-center rounded-full border shadow-[var(--shadow-lift)]"
        >
          <span aria-hidden className="text-body ml-3 leading-none">
            {FLAGS[current.code]}
          </span>
          <span className="text-fg text-subhead ml-2 font-medium">{current.short}</span>
          <ChevronDown aria-hidden className="text-fg-subtle mr-3 ml-1 size-3.5" />
        </span>
      }
    >
      {LOCALES.map((entry) => (
        <MenuItem
          key={entry.code}
          onClick={() => setLocale(entry.code)}
          icon={<span aria-hidden>{FLAGS[entry.code]}</span>}
          trailing={
            entry.code === current.code ? <Check className="size-4" aria-hidden /> : null
          }
        >
          {entry.label}
        </MenuItem>
      ))}
    </Menu>
  );
}
